import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

export const billingGstr1Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing/gstr1',
  component: BillingGstr1Page,
});

function currentMonth(): string {
  const d = new Date();
  const off = (5 * 60 + 30) * 60 * 1000;
  return new Date(d.getTime() + off).toISOString().slice(0, 7);
}

function BillingGstr1Page(): JSX.Element {
  const [month, setMonth] = React.useState<string>(currentMonth());
  const [error, setError] = React.useState<string | null>(null);

  const downloadMutation = useMutation({
    mutationFn: (m: string) => api.downloadGstr1(m),
    onSuccess: (blob) => {
      setError(null);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gstr1-${month}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    onError: (err) => setError(toMessage(err)),
  });

  return (
    <AppShell>
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">GSTR-1 export</h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            Starter CSV with B2B, CDNR, B2CL, B2CS sections. Your CA does the final formatting.
          </p>
        </div>
      </div>

      {error ? (
        <Alert variant="destructive"><AlertTitle>Error</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Month</CardTitle>
          <CardDescription>Pick the month to export.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label>Month</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.currentTarget.value)} />
          </div>
          <Button onClick={() => downloadMutation.mutate(month)} disabled={downloadMutation.isPending || !month}>
            {downloadMutation.isPending ? 'Downloading…' : 'Download CSV'}
          </Button>
        </CardContent>
      </Card>
    </AppShell>
  );
}
